import { useContext } from 'react'

import { Button, ModalContext, SidebarModal } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import usePublicationBooking, { usePublicationBookingForm } from '@hooks/usePublicationBooking'
import usePublicationBookingItems from '@hooks/usePublicationBookingItems'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import BookingDetails from './components/BookingDetails'
import CampaignAddresses from './components/CampaignAddresses'
import Options from './components/Options'
import Status from './components/Status'

const MODAL_KEY = 'ManagePublicationBookingModal'

export type ModalCallbacks = {
  closeModal: () => void,
  ftpSync: (payload: HandleCallbackActionParams) => void,
  pdf: (payload: HandleCallbackActionParams) => void,
  updatePublicationBooking: (payload: HandleCallbackActionParams) => void,
}

interface ModalProps {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManagePublicationBookingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updatePublicationBooking } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { publicationBooking: initPublicationBooking } = modalPayload

  const publicationBookingPayload = usePublicationBooking(initPublicationBooking)
  const {
    callbacks: {
      updatePublicationBooking: updateFn,
    },
    publicationBooking,
    updating,
  } = publicationBookingPayload

  const publicationFormPayload = usePublicationBookingForm(publicationBooking, { validateOn: 'change' })
  const {
    entityState,
  } = publicationFormPayload

  const publicationBookingItemsPayload = usePublicationBookingItems({
    filters: { publication_booking_id: publicationBooking?.id },
  })
  const { hasFilteredPublicationBookingItems } = publicationBookingItemsPayload

  const currentUserPayload = useCurrentUser()
  const { isAdmin } = currentUserPayload

  const updatePublicationBookingPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Publication Booking Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Booking" titleSecondLine="Details" />

      <SidebarModal.Content>
        {hasFilteredPublicationBookingItems && (
          <CampaignAddresses publicationBookingItemsPayload={publicationBookingItemsPayload} />
        )}

        <BookingDetails
          isAdmin={isAdmin}
          publicationFormPayload={publicationFormPayload}
          publicationBookingPayload={publicationBookingPayload}
        />

        <Status publicationBooking={publicationBooking} />

        <Options callbacks={callbacks} isAdmin={isAdmin} publicationBookingPayload={publicationBookingPayload} />
      </SidebarModal.Content>

      <SidebarModal.Footer>

        <Button
          buttonStyle={isAdmin ? 'primaryCreate' : 'secondaryUtility'}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => (isAdmin ? updatePublicationBooking(updatePublicationBookingPayload) : closeModal())}
          size="large"
        >
          {isAdmin ? 'Save' : 'Close Modal'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManagePublicationBookingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
